/* jshint esversion: 6 */
/* jshint esversion: 8 */
/* jshint ignore:start */
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MyContext } from "../Context/MyProvider";
import axios from "axios";
import { showTost } from "../showTost";
import { BiChevronLeft, BiChevronRight, BiEditAlt, BiSearch, BiX } from "react-icons/bi";
import { HiOutlineLocationMarker } from "react-icons/hi";
import { FaHeart } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { defaultDistanceSettings, fetchDistanceSettings } from "../utils/distanceSettings";

const getRegisterUser = () => {
  try {
    return JSON.parse(localStorage.getItem("Register_User") || "{}");
  } catch {
    return {};
  }
};

const getFirstImage = (item) => {
  if (!item) return '';
  if (Array.isArray(item.profile_images) && item.profile_images.length > 0) {
    const first = item.profile_images[0];
    return typeof first === 'string' ? first : (first?.url || '');
  }
  if (typeof item.profile_images === 'string' && item.profile_images.trim().length > 0) {
    return item.profile_images.split('$;')[0];
  }
  if (typeof item.profile_pic === 'string') return item.profile_pic;
  if (typeof item.identity_picture === 'string') return item.identity_picture;
  return '';
};

const calculateDistanceKm = (lat1, lon1, lat2, lon2) => {
  const p1Lat = parseFloat(lat1);
  const p1Lon = parseFloat(lon1);
  const p2Lat = parseFloat(lat2);
  const p2Lon = parseFloat(lon2);
  if (isNaN(p1Lat) || isNaN(p1Lon) || isNaN(p2Lat) || isNaN(p2Lon)) return 99999;
  if ((p1Lat === 0 && p1Lon === 0) || (p2Lat === 0 && p2Lon === 0)) return 99999;
  const R = 6371;
  const dLat = ((p2Lat - p1Lat) * Math.PI) / 180;
  const dLon = ((p2Lon - p1Lon) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((p1Lat * Math.PI) / 180) *
      Math.cos((p2Lat * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const clampedA = Math.min(1, Math.max(0, a));
  const c = 2 * Math.atan2(Math.sqrt(clampedA), Math.sqrt(1 - clampedA));
  return Math.round(R * c * 10) / 10;
};

const getInitialCoordsAndName = () => {
  try {
    const savedPassportActive = localStorage.getItem("saved_passport_active") === "true";
    const savedPassportName = localStorage.getItem("saved_passport_location_name");
    const savedPassportLat = localStorage.getItem("saved_passport_lat");
    const savedPassportLong = localStorage.getItem("saved_passport_long");

    if (savedPassportActive && savedPassportLat && savedPassportLong) {
      return {
        lat: parseFloat(savedPassportLat),
        long: parseFloat(savedPassportLong),
        locationName: savedPassportName || "Surat, Gujarat, India"
      };
    }
    const userLat = localStorage.getItem("user_latitude");
    const userLong = localStorage.getItem("user_longitude");
    if (userLat && userLong) {
      return {
        lat: parseFloat(userLat),
        long: parseFloat(userLong),
        locationName: "Current Location"
      };
    }
  } catch {}
  return { lat: 21.1702, long: 72.8311, locationName: "Surat, Gujarat, India" };
};

const MapScreen = () => {
  const { t } = useTranslation();
  const { basUrl, imageBaseURL, setProfileId } = useContext(MyContext);
  const navigate = useNavigate();

  const initialData = useMemo(() => getInitialCoordsAndName(), []);
  const [user, setUser] = useState({});
  const [distanceSettings, setDistanceSettings] = useState(defaultDistanceSettings);
  const [distance, setDistance] = useState(defaultDistanceSettings.defaultDistance);
  const [isEdit, setIsEdit] = useState(false);
  const [locationName, setLocationName] = useState(initialData.locationName);
  const [lat, setLat] = useState(initialData.lat);
  const [long, setLong] = useState(initialData.long);

  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [nearbyUsers, setNearbyUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [mapProvider, setMapProvider] = useState("google");
  const [mapKey, setMapKey] = useState("");
  const [masterPassportEnabled, setMasterPassportEnabled] = useState(false);
  const [homeDataState, setHomeDataState] = useState(null);

  const mapContainerRef = useRef(null);
  const leafletMapRef = useRef(null);
  const tileLayerRef = useRef(null);
  const markerRef = useRef(null);
  const circleRef = useRef(null);
  const userMarkersRef = useRef([]);
  const carouselRef = useRef(null);

  const scrollLeft = () => {
    if (carouselRef.current) {
      carouselRef.current.scrollBy({ left: -320, behavior: "smooth" });
    }
  };

  const scrollRight = () => {
    if (carouselRef.current) {
      carouselRef.current.scrollBy({ left: 320, behavior: "smooth" });
    }
  };

  // 1. Load User Data, Distance Settings & Admin Map Provider
  useEffect(() => {
    const userData = getRegisterUser();
    setUser(userData);

    const savedPassportActive = localStorage.getItem("saved_passport_active") === "true";
    const savedPassportName = localStorage.getItem("saved_passport_location_name");
    const savedPassportLat = localStorage.getItem("saved_passport_lat");
    const savedPassportLong = localStorage.getItem("saved_passport_long");

    if (savedPassportActive && savedPassportLat && savedPassportLong) {
      setLat(parseFloat(savedPassportLat));
      setLong(parseFloat(savedPassportLong));
      if (savedPassportName) setLocationName(savedPassportName);
    } else {
      const userLat = localStorage.getItem("user_latitude");
      const userLong = localStorage.getItem("user_longitude");
      if (userLat && userLong) {
        setLat(parseFloat(userLat));
        setLong(parseFloat(userLong));
      }
    }

    fetchDistanceSettings(basUrl).then((settings) => {
      setDistanceSettings(settings);
      const savedRadius = localStorage.getItem("rediuse");
      if (savedRadius) {
        setDistance(parseFloat(savedRadius));
      } else {
        setDistance(settings.defaultDistance);
      }
    });

    axios.post(`${basUrl}sms_type`)
      .then((res) => {
        if (res.data) {
          if (res.data.map_provider) setMapProvider(String(res.data.map_provider).toLowerCase());
          if (res.data.map_key) setMapKey(String(res.data.map_key));
          if (res.data.passport_mode_enabled !== undefined) {
            setMasterPassportEnabled(String(res.data.passport_mode_enabled) === "1");
          }
        }
      })
      .catch(console.error);
  }, [basUrl]);

  // 2. Fetch Nearby Users for Map
  const fetchNearbyUsers = useCallback(async (currentLat, currentLong, currentRadius) => {
    setLoadingUsers(true);
    try {
      const userData = getRegisterUser();
      const uid = userData?.id || "";
      const res = await axios.post(`${basUrl}home_data`, {
        uid: uid,
        lats: String(currentLat),
        longs: String(currentLong),
        distance: String(currentRadius),
      });
      if (res.data?.Result === "true" || res.data?.ResponseCode === "200") {
        setHomeDataState(res.data);
        const list = res.data.profilelist || res.data.home_data || res.data.data || [];
        setNearbyUsers(list);
        if (res.data?.passport_mode_enabled !== undefined) {
          setMasterPassportEnabled(String(res.data.passport_mode_enabled) === "1");
        }
      }
    } catch (e) {
      console.error("Error fetching nearby users:", e);
    } finally {
      setLoadingUsers(false);
    }
  }, [basUrl]);

  useEffect(() => {
    fetchNearbyUsers(lat, long, distance);
  }, [lat, long, distance, fetchNearbyUsers]);

  // Strictly filter users falling within the selected KM radius of active map location (lat, long)
  const filteredUsers = useMemo(() => {
    const list = nearbyUsers.filter((u) => {
      const uLat = parseFloat(u.profile_lat || u.lats || u.lat);
      const uLong = parseFloat(u.profile_longs || u.longs || u.long);

      if (isNaN(uLat) || isNaN(uLong) || (uLat === 0 && uLong === 0)) return false;

      const distFromPin = calculateDistanceKm(lat, long, uLat, uLong);
      return distFromPin <= distance;
    });

    if (list.length === 0 && nearbyUsers.length > 0) {
      return nearbyUsers;
    }
    return list;
  }, [nearbyUsers, lat, long, distance]);

  // Reset carousel horizontal scroll to the start (left side) whenever profiles load or change
  useEffect(() => {
    if (carouselRef.current) {
      carouselRef.current.scrollLeft = 0;
    }
    const timer = setTimeout(() => {
      if (carouselRef.current) {
        carouselRef.current.scrollLeft = 0;
      }
    }, 100);
    return () => clearTimeout(timer);
  }, [filteredUsers, loadingUsers]);

  // 3. Reverse Geocode Coordinates to Address Name
  const reverseGeocode = async (targetLat, targetLng) => {
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${targetLat}&lon=${targetLng}&zoom=12`
      );
      const data = await response.json();
      if (data && data.display_name) {
        const parts = data.display_name.split(",");
        const name = parts.slice(0, 3).join(",").trim();
        setLocationName(name);
        setSearchQuery(name);
      }
    } catch (e) {
      console.warn("Reverse geocode error:", e);
    }
  };

  // 4. Initialize & Synchronize Leaflet Interactive Map
  useEffect(() => {
    const loadLeaflet = () => {
      if (window.L) return Promise.resolve(window.L);

      if (!document.getElementById("leaflet-css")) {
        const link = document.createElement("link");
        link.id = "leaflet-css";
        link.rel = "stylesheet";
        link.href = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
        document.head.appendChild(link);
      }

      return new Promise((resolve) => {
        if (window.L) return resolve(window.L);
        const script = document.createElement("script");
        script.src = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js";
        script.onload = () => resolve(window.L);
        document.body.appendChild(script);
      });
    };

    loadLeaflet().then((L) => {
      if (!mapContainerRef.current) return;

      if (!leafletMapRef.current) {
        const map = L.map(mapContainerRef.current, {
          center: [lat, long],
          zoom: 11,
          zoomControl: false,
        });

        L.control.zoom({ position: "bottomright" }).addTo(map);
        leafletMapRef.current = map;

        map.on("click", (e) => {
          if (!masterPassportEnabled) {
            showTost({ title: "Passport Mode is currently disabled globally by administrator." });
            return;
          }
          if (!isEdit) return;
          const newLat = e.latlng.lat;
          const newLng = e.latlng.lng;
          setLat(newLat);
          setLong(newLng);
          reverseGeocode(newLat, newLng);
        });
      } else {
        leafletMapRef.current.setView([lat, long]);
      }

      const map = leafletMapRef.current;

      // Dynamically handle Map Provider (Google Maps vs OpenStreetMap / OSM)
      const isGoogle = mapProvider === "google";
      const tileUrl = isGoogle
        ? `https://mt1.google.com/vt/lyrs=m&x={x}&y={y}&z={z}${mapKey ? '&key=' + mapKey : ''}`
        : "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png";

      const tileAttribution = isGoogle
        ? '&copy; <a href="https://maps.google.com">Google Maps</a>'
        : '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>';

      if (tileLayerRef.current) {
        map.removeLayer(tileLayerRef.current);
      }

      tileLayerRef.current = L.tileLayer(tileUrl, {
        attribution: tileAttribution,
        maxZoom: 19,
        subdomains: isGoogle ? ['mt0', 'mt1', 'mt2', 'mt3'] : ['a', 'b', 'c']
      }).addTo(map);

      // Custom Purple Map Pin Icon
      const customIcon = L.divIcon({
        className: "custom-map-pin",
        html: `<div style="background: #980eff; width: 36px; height: 36px; border-radius: 50% 50% 50% 0; transform: rotate(-45deg); display: flex; align-items: center; justify-content: center; box-shadow: 0 4px 12px rgba(152,14,255,0.4); border: 3px solid #ffffff;">
                <div style="background: #ffffff; width: 12px; height: 12px; border-radius: 50%;"></div>
               </div>`,
        iconSize: [36, 36],
        iconAnchor: [18, 36],
      });

      if (markerRef.current) {
        markerRef.current.setLatLng([lat, long]);
      } else {
        markerRef.current = L.marker([lat, long], { icon: customIcon }).addTo(map);
      }

      // Radius Circle (Shown only when isEdit is TRUE)
      if (isEdit) {
        const radiusMeters = distance * 1000;
        if (circleRef.current) {
          circleRef.current.setLatLng([lat, long]);
          circleRef.current.setRadius(radiusMeters);
        } else {
          circleRef.current = L.circle([lat, long], {
            color: "#980eff",
            fillColor: "#980eff",
            fillOpacity: 0.18,
            weight: 2,
            radius: radiusMeters,
          }).addTo(map);
        }
      } else {
        if (circleRef.current) {
          map.removeLayer(circleRef.current);
          circleRef.current = null;
        }
      }

      // Render Nearby User Markers on Map (Strictly filtered by selected KM radius)
      userMarkersRef.current.forEach((m) => map.removeLayer(m));
      userMarkersRef.current = [];

      filteredUsers.forEach((u) => {
        const uLat = parseFloat(u.profile_lat || u.lats || u.lat);
        const uLong = parseFloat(u.profile_longs || u.longs || u.long);

        const picPath = getFirstImage(u);
        const imgUrl = picPath ? (picPath.startsWith('http') ? picPath : imageBaseURL + picPath) : 'https://via.placeholder.com/40';
        const cleanName = (u.profile_name || u.name || u.title || 'User').trim().replace(/,$/, '');

        const age = u.profile_age || u.age || 25;
        const distKm = calculateDistanceKm(lat, long, uLat, uLong);
        const distText = distKm < 9999 ? `${distKm} km away` : '';

        const userIcon = L.divIcon({
          className: "user-map-pin",
          html: `<div style="position: relative; width: 42px; height: 42px; border-radius: 50%; border: 3px solid #980eff; overflow: hidden; background: #fff; box-shadow: 0 4px 12px rgba(152,14,255,0.4); cursor: pointer;">
                  <img src="${imgUrl}" style="width: 100%; height: 100%; object-fit: cover;" onerror="this.onerror=null;this.src='https://via.placeholder.com/42';" />
                 </div>`,
          iconSize: [42, 42],
          iconAnchor: [21, 21],
        });
        const uMarker = L.marker([uLat, uLong], { icon: userIcon }).addTo(map);

        const tooltipHtml = `
          <div style="
            display: flex;
            align-items: center;
            gap: 8px;
            padding: 6px 10px;
            background: #1e1b4b;
            color: #ffffff;
            font-family: inherit;
            border-radius: 10px;
            box-shadow: 0 10px 25px rgba(0,0,0,0.3);
            border: 1px solid rgba(152, 14, 255, 0.5);
            white-space: nowrap;
          ">
            <div style="width: 28px; height: 28px; border-radius: 50%; overflow: hidden; border: 2px solid #980eff; shrink: 0; background: #fff;">
              <img src="${imgUrl}" style="width: 100%; height: 100%; object-fit: cover;" onerror="this.onerror=null;this.src='https://via.placeholder.com/28';" />
            </div>
            <div>
              <div style="font-weight: 700; font-size: 12px; line-height: 1.2; color: #ffffff;">
                ${cleanName}, ${age}
              </div>
              ${distText ? `<div style="font-size: 10px; color: #c084fc; margin-top: 1px;">📍 ${distText}</div>` : ''}
            </div>
          </div>
        `;

        uMarker.bindTooltip(tooltipHtml, {
          direction: 'top',
          offset: [0, -26],
          className: 'custom-map-tooltip',
          opacity: 1
        });

        uMarker.on("click", () => {
          const targetId = u.profile_id || u.id;
          if (targetId) {
            sessionStorage.setItem("DetailsId", targetId);
            sessionStorage.setItem("Icon-Color", "Explore");
            setProfileId(targetId);
            navigate(`/detail/${encodeURIComponent(cleanName)}`, { state: { ProfileId: targetId, sourceTab: "Map" } });
          }
        });
        userMarkersRef.current.push(uMarker);
      });
    });
  }, [lat, long, distance, isEdit, filteredUsers, imageBaseURL, navigate, setProfileId, mapProvider, mapKey]);

  // 5. Search City / Location Autocomplete
  const handleSearch = async (queryText) => {
    setSearchQuery(queryText);
    if (!queryText.trim()) {
      setSearchResults([]);
      return;
    }
    setIsSearching(true);
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(queryText)}&limit=5`
      );
      const data = await response.json();
      setSearchResults(data || []);
    } catch (e) {
      console.warn("Search geocode error:", e);
    } finally {
      setIsSearching(false);
    }
  };

  const selectSearchResult = (item) => {
    const selectedLat = parseFloat(item.lat);
    const selectedLong = parseFloat(item.lon);
    setLat(selectedLat);
    setLong(selectedLong);
    setLocationName(item.display_name);
    setSearchQuery(item.display_name);
    setSearchResults([]);
  };

  const isSubscribedOrFree = useMemo(() => {
    const planId = String(user?.plan_id || user?.planId || homeDataState?.plan_id || localStorage.getItem("PurchaseId") || "0");
    const isSubscribe = String(homeDataState?.is_subscribe || "0");
    const freeMode = String(homeDataState?.free_mode || "0");
    const directChat = String(homeDataState?.direct_chat || "0");
    return planId !== "0" || isSubscribe === "1" || freeMode === "1" || directChat === "1";
  }, [user, homeDataState]);

  // 6. Passport Plan Gating Action Handlers
  const handleEditClick = () => {
    if (isEdit) {
      // Cancel edit mode
      setIsEdit(false);
      setSearchResults([]);
      setSearchQuery("");
    } else {
      // Check Plan Entitlement
      if (!isSubscribedOrFree) {
        showTost({ title: "Upgrade plan required to use Passport Mode." });
        navigate("/upgrade");
      } else {
        setIsEdit(true);
      }
    }
  };

  const handleContinueClick = async () => {
    if (!isSubscribedOrFree) {
      showTost({ title: "Upgrade plan required to use Passport Mode." });
      navigate("/upgrade");
      return;
    }

    try {
      localStorage.setItem("rediuse", String(distance));
      localStorage.setItem("saved_passport_active", "true");
      localStorage.setItem("saved_passport_location_name", locationName);
      localStorage.setItem("saved_passport_lat", String(lat));
      localStorage.setItem("saved_passport_long", String(long));

      if (user?.id) {
        await axios.post(`${basUrl}update_passport_location`, {
          uid: user.id,
          passport_location_name: locationName,
          passport_lat: String(lat),
          passport_long: String(long),
          radius_search: String(distance),
          is_passport_active: 1
        });
      }

      showTost({ title: `Passport location saved to ${locationName}!` });
      setIsEdit(false);
      fetchNearbyUsers(lat, long, distance);
    } catch (e) {
      console.error("Failed to save passport location:", e);
      showTost({ title: "Unable to update passport location." });
    }
  };

  return (
    <div style={{ userSelect: "none", cursor: "default" }}>
      <div className="content-body p-0 overflow-hidden">
        <div className="relative w-full h-[calc(100vh-75px)] bg-gray-100 overflow-hidden font-sans">
          {/* Interactive Map */}
          <div ref={mapContainerRef} className="w-full h-full z-0" />

          {/* Floating Top Control Card */}
          <div className="absolute top-2 sm:top-4 left-1/2 transform -translate-x-1/2 z-20 w-[94%] sm:w-[92%] max-w-[500px]">
            <div className="bg-white/95 backdrop-blur-md rounded-2xl sm:rounded-3xl p-3 sm:p-5 shadow-2xl border border-white/80 transition-all duration-300">
              {/* Header Row: Title & Edit/Cancel Button */}
              <div className="flex items-center justify-between gap-2 sm:gap-3">
                <div className="flex items-center gap-2.5 sm:gap-3 overflow-hidden">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-[rgba(152,14,255,0.12)] flex items-center justify-center shrink-0">
                    <HiOutlineLocationMarker className="text-[rgba(152,14,255,1)] text-lg sm:text-2xl" />
                  </div>
                  <div className="overflow-hidden">
                    <p className="text-[10px] sm:text-xs font-bold text-gray-500 uppercase tracking-wider leading-tight">
                      Location (Within {Math.round(distance)} km)
                    </p>
                    <h4 className="text-xs sm:text-sm font-bold text-gray-900 truncate mt-0.5">
                      {locationName}
                    </h4>
                  </div>
                </div>

                {masterPassportEnabled && (
                <button
                  onClick={handleEditClick}
                  className={`px-3 py-1.5 sm:px-4 sm:py-2 rounded-full font-bold text-[11px] sm:text-xs flex items-center gap-1 transition-all shadow-sm shrink-0 ${
                    isEdit
                      ? "bg-gray-500 text-white hover:bg-gray-600"
                      : "bg-[rgba(152,14,255,1)] text-white hover:bg-[rgba(130,10,225,1)]"
                  }`}
                >
                  <BiEditAlt className="text-sm sm:text-base" />
                  {isEdit ? "Cancel" : t("Edit")}
                </button>
                )}
              </div>

              {/* Conditional Edit Controls */}
              {isEdit && (
                <div className="mt-3 sm:mt-4 pt-3 sm:pt-4 border-t border-gray-100 space-y-3 sm:space-y-4">
                  {/* Search City / Location Input */}
                  <div className="relative">
                    <div className="flex items-center bg-gray-50 border border-gray-200 rounded-2xl px-3 sm:px-3.5 py-2 sm:py-2.5 focus-within:border-[rgba(152,14,255,1)] focus-within:ring-2 focus-within:ring-[rgba(152,14,255,0.2)] transition-all">
                      <BiSearch className="text-gray-400 text-lg sm:text-xl shrink-0 mr-2" />
                      <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => handleSearch(e.target.value)}
                        placeholder="Search city or location..."
                        className="w-full bg-transparent text-xs sm:text-sm text-gray-800 focus:outline-none"
                      />
                      {isSearching ? (
                        <div className="w-4 h-4 border-2 border-purple-600 border-t-transparent rounded-full animate-spin shrink-0" />
                      ) : searchQuery ? (
                        <button
                          onClick={() => handleSearch("")}
                          className="text-gray-400 hover:text-gray-600 p-1"
                        >
                          <BiX className="text-lg" />
                        </button>
                      ) : null}
                    </div>

                    {/* Suggestions Dropdown */}
                    {searchResults.length > 0 && (
                      <div className="absolute left-0 right-0 top-full mt-2 bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden z-30 max-h-48 overflow-y-auto">
                        {searchResults.map((item, idx) => (
                          <button
                            key={idx}
                            onClick={() => selectSearchResult(item)}
                            className="w-full text-left px-4 py-3 text-xs text-gray-700 hover:bg-purple-50 border-b border-gray-50 last:border-none flex items-center gap-2 transition-colors"
                          >
                            <HiOutlineLocationMarker className="text-[rgba(152,14,255,1)] shrink-0 text-base" />
                            <span className="truncate">{item.display_name}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Helper Tip */}
                  <p className="text-[10px] sm:text-[11px] text-purple-700 bg-purple-50 px-3 py-1.5 rounded-xl font-medium flex items-center gap-1.5">
                    <span>💡</span> Tap anywhere on map or select a suggestion above to set pin
                  </p>

                  {/* Floating Distance Badge & Slider */}
                  <div className="pt-1 sm:pt-2">
                    <div className="flex justify-center -mb-2">
                      <span className="bg-[rgba(152,14,255,1)] text-white text-[11px] sm:text-xs font-bold px-3 py-1 rounded-full shadow-lg">
                        {Math.round(distance)} km
                      </span>
                    </div>
                    <input
                      type="range"
                      min={distanceSettings.minDistance || 1}
                      max={distanceSettings.maxDistance || 5000}
                      step="1"
                      value={distance}
                      onChange={(e) => setDistance(Number(e.target.value))}
                      className="w-full accent-[rgba(152,14,255,1)] cursor-pointer h-2 bg-gray-200 rounded-lg appearance-none"
                    />
                    <div className="flex justify-between text-[10px] font-bold text-gray-400 mt-1 px-1">
                      <span>{distanceSettings.minDistance || 1} km</span>
                      <span>{distanceSettings.maxDistance || 5000} km</span>
                    </div>
                  </div>

                  {/* Continue Button */}
                  <button
                    onClick={handleContinueClick}
                    className="w-full bg-[rgba(152,14,255,1)] hover:bg-[rgba(130,10,225,1)] text-white font-bold text-xs sm:text-sm py-2.5 sm:py-3 rounded-2xl shadow-xl transition-all active:scale-[0.98]"
                  >
                    Continue
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Bottom Nearby Users Full Width Carousel with Left / Right Navigation Arrows */}
          <div className="absolute bottom-2 sm:bottom-4 left-0 right-0 z-20 px-2 sm:px-6 w-full max-w-[1350px] mx-auto pointer-events-none">
            {loadingUsers ? (
              <div className="bg-white/90 backdrop-blur-md rounded-2xl p-3 sm:p-4 text-center text-xs text-gray-500 shadow-xl pointer-events-auto">
                Loading nearby profiles...
              </div>
            ) : filteredUsers.length === 0 ? (
              <div className="bg-white/90 backdrop-blur-md rounded-2xl p-3 sm:p-4 text-center text-xs text-gray-500 shadow-xl pointer-events-auto">
                No active profiles found within {Math.round(distance)} km.
              </div>
            ) : (
              <div className="flex items-center gap-2 pointer-events-auto">
                {/* Left Scroll Arrow */}
                <button
                  onClick={scrollLeft}
                  className="hidden sm:flex w-10 h-10 rounded-full bg-white/95 backdrop-blur-md shadow-xl text-gray-800 items-center justify-center hover:bg-[rgba(152,14,255,1)] hover:text-white transition-all cursor-pointer border border-gray-200 shrink-0"
                  title="Previous Profile"
                >
                  <BiChevronLeft className="text-2xl" />
                </button>

                {/* Horizontal Scroll Area */}
                <div
                  ref={carouselRef}
                  className="flex-1 flex gap-2.5 sm:gap-3.5 overflow-x-auto py-1.5 sm:py-2 px-1 custom-theme-scrollbar snap-x scroll-smooth"
                >
                  {filteredUsers.map((item, idx) => {
                    const picPath = getFirstImage(item);
                    const imgUrl = picPath ? (picPath.startsWith('http') ? picPath : imageBaseURL + picPath) : '';
                    const profileId = item.profile_id || item.id || idx;
                    const cleanName = (item.profile_name || item.name || item.title || 'User').trim().replace(/,$/, '');
                    const age = item.profile_age || item.age || 25;
                    
                    const itemLat = parseFloat(item.profile_lat || item.lats || item.lat);
                    const itemLong = parseFloat(item.profile_longs || item.longs || item.long);
                    const distKm = calculateDistanceKm(lat, long, itemLat, itemLong);
                    const distText = distKm < 9999 ? `${distKm} km away` : '';
                    const cityOrBio = (item.city || item.profile_bio || item.bio || '').trim().replace(/^[•,\s]+|[•,\s]+$/g, '');

                    return (
                      <div
                        key={profileId}
                        onClick={() => {
                          const targetId = item.profile_id || item.id;
                          if (targetId) {
                            sessionStorage.setItem("DetailsId", targetId);
                            sessionStorage.setItem("Icon-Color", "Explore");
                            setProfileId(targetId);
                            navigate(`/detail/${encodeURIComponent(cleanName)}`, { state: { ProfileId: targetId, sourceTab: "Map" } });
                          }
                        }}
                        className="snap-start shrink-0 w-[260px] sm:w-[320px] bg-white/95 backdrop-blur-md rounded-2xl p-2.5 sm:p-3 shadow-xl border border-white/70 cursor-pointer hover:shadow-2xl hover:scale-[1.02] transition-all flex items-center gap-2.5 sm:gap-3"
                      >
                        <div className="w-11 h-11 sm:w-[52px] sm:h-[52px] rounded-full overflow-hidden shrink-0 border-2 border-[rgba(152,14,255,0.4)] bg-purple-50 flex items-center justify-center shadow-inner">
                          {imgUrl ? (
                            <img
                              src={imgUrl}
                              alt={cleanName}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = 'https://via.placeholder.com/52';
                              }}
                            />
                          ) : (
                            <span className="text-purple-600 font-bold text-base sm:text-lg">
                              {cleanName.charAt(0)}
                            </span>
                          )}
                        </div>
                        <div className="overflow-hidden flex-1">
                          <h5 className="text-xs sm:text-[15px] font-bold text-gray-900 truncate tracking-tight">
                            {cleanName}, {age}
                          </h5>
                          <p className="text-[11px] sm:text-[12px] text-gray-500 truncate mt-0.5 sm:mt-1 flex items-center gap-1 font-medium">
                            <span className="text-purple-600 font-semibold">{distText}</span>
                            {cityOrBio && (
                              <>
                                <span className="text-gray-300">•</span>
                                <span className="truncate">{cityOrBio}</span>
                              </>
                            )}
                          </p>
                        </div>
                        <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-[rgba(152,14,255,1)] text-white flex items-center justify-center shrink-0 shadow-md hover:scale-110 transition-transform">
                          <FaHeart className="text-[10px] sm:text-xs" />
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Right Scroll Arrow */}
                <button
                  onClick={scrollRight}
                  className="hidden sm:flex w-10 h-10 rounded-full bg-white/95 backdrop-blur-md shadow-xl text-gray-800 items-center justify-center hover:bg-[rgba(152,14,255,1)] hover:text-white transition-all cursor-pointer border border-gray-200 shrink-0"
                  title="Next Profile"
                >
                  <BiChevronRight className="text-2xl" />
                </button>
              </div>
            )}
          </div>
    </div>
  </div>
</div>
  );
};

export default MapScreen;
